import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Empty,
  Field,
  Note,
  Panel,
  Spinner,
  TableWrap,
  shortId,
} from '@wyocspay/ui';
import { badgeLabel, dateTime, relative } from './format.js';
import {
  ApiError,
  api,
  type AdminTerminal,
  type TapToPayAvailability,
  type TerminalHistory,
  type TerminalMerchant,
  type TerminalReview,
} from './api.js';
import { buildCommand, requireIdentity, signCommand } from './device.js';
import type { Signer } from './oversight.js';

/**
 * Картовите терминали на цялата платформа.
 *
 * Четенето е обикновена административна сесия. Отнемането - не: то спира един
 * телефон да приема пари при следващата му заявка, търговецът не го е искал и не
 * може да го върне сам от своя панел, така че минава през подписана контролна
 * команда като всяко друго привилегировано действие тук. Самото отнемане се
 * извършва от services/terminals.ts, същата функция, която ползва и панелът на
 * търговеца - едно място, което знае как се обезсилва токен и какво се записва.
 *
 * Страницата показва и защо един търговец НЕ може да регистрира телефон, защото
 * това почти никога не е нещо в таблицата с терминали: или платформата не му е
 * дала функцията, или картовите схеми не допускат Tap to Pay в неговата държава.
 * Второто не може да се промени с одобрение и затова се показва като факт.
 */

function deviceLine(t: AdminTerminal): string {
  return (
    [t.device_model, t.platform ? t.platform.toUpperCase() : null, t.os_version]
      .filter(Boolean)
      .join(' · ') || 'неизвестно устройство'
  );
}

/**
 * `install_id` се избира от приложението и няма нашия формат с представка, така
 * че `shortId` не го скъсява. Показва се началото му, а цялото стои в title.
 */
function shortInstall(id: string): string {
  return id.length <= 18 ? id : `${id.slice(0, 10)}…${id.slice(-6)}`;
}

/** Кои платформи са възможни в дадена държава, като кратък човешки текст. */
function availabilityText(a: TapToPayAvailability): string {
  if (a.unavailable) return 'не се предлага';
  if (a.ios && a.android) return 'iPhone и Android';
  return a.ios ? 'само iPhone' : 'само Android';
}

/**
 * Кратко обяснение на български до суровото име на действието.
 *
 * Суровото име остава - то е това, което се търси в одитния дневник и се цитира
 * пред одитор. Тук се добавя изречение, защото този панел се чете от човек,
 * който проверява какво се е случило с един апарат, а не имена на събития.
 */
const ACTION_NOTE: Record<string, string> = {
  'terminal.registered': 'Търговецът е въвел кода от телефона и апаратът е станал терминал.',
  'terminal.revoked': 'Терминалът е отнет. Токенът му е заменен и спира да работи при следващата заявка.',
  'terminal.payment_started': 'Започнато плащане през този терминал.',
  'terminal.payment_captured': 'Плащането е прието от Stripe.',
  'terminal.payment_cancelled': 'Авторизацията е отменена.',
};

export function AdminTerminalsView({
  signer,
  onOpenMerchant,
}: {
  signer: Signer;
  onOpenMerchant?: (id: string) => void;
}) {
  const [merchantId, setMerchantId] = useState('');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');

  const [data, setData] = useState<{ data: AdminTerminal[]; merchants: TerminalMerchant[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [confirming, setConfirming] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'warn' | 'danger'; text: string } | null>(null);

  const [openHistory, setOpenHistory] = useState<string | null>(null);
  const [history, setHistory] = useState<TerminalHistory | null>(null);

  /** The pending terminal whose review panel (address + approve/reject) is open. */
  const [openReview, setOpenReview] = useState<string | null>(null);
  const [review, setReview] = useState<TerminalReview | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.terminals({ merchant_id: merchantId || undefined, status, search }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [merchantId, status, search]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Отнемане на терминал - подписана команда, не POST.
   *
   * `previousVersion` е нула, защото терминалът няма колона за версия. Това не е
   * пропуск: действието е идемпотентно, а измислена версия би обещала гаранция
   * за конкурентност, каквато таблицата не може да даде.
   */
  async function revoke(terminal: AdminTerminal): Promise<void> {
    setBusy(terminal.id);
    setMsg(null);
    try {
      const identity = requireIdentity(signer.identity);
      const cmd = buildCommand({
        actorId: signer.actorId,
        deviceId: identity.deviceId,
        target: `terminal:${terminal.id}`,
        transition: 'revoke',
        previousVersion: 0,
        params: { reason: reason.trim() || 'отнет от администрацията' },
      });
      await api.command(cmd, await signCommand(identity, cmd));
      setMsg({
        tone: 'warn',
        text: `„${terminal.label}“ на ${terminal.merchant.name} е отнет. Телефонът спира да приема плащания при следващата си заявка и може да бъде сдвоен наново.`,
      });
      setConfirming(null);
      setReason('');
      if (openHistory === terminal.id) await showHistory(terminal.id);
      await load();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  }

  async function showHistory(id: string): Promise<void> {
    setOpenHistory(id);
    setHistory(null);
    try {
      setHistory(await api.terminalHistory(id));
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
      setOpenHistory(null);
    }
  }

  async function showReview(id: string): Promise<void> {
    setOpenReview(id);
    setReview(null);
    setRejectReason('');
    try {
      setReview(await api.terminalReview(id));
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
      setOpenReview(null);
    }
  }

  /**
   * Approve or refuse a pending pairing.
   *
   * A plain, permission-gated action rather than a signed command: the platform
   * owner asked for terminal review to work from the admin session and client
   * certificate alone, without a second signing device. The server still checks
   * the `terminals.approve` permission and writes the same audit trail; only the
   * external signature is gone. Revoking an already-trusted terminal is still
   * signed - that is the destructive one.
   */
  async function decide(terminal: AdminTerminal, approve: boolean): Promise<void> {
    setBusy(terminal.id);
    setMsg(null);
    try {
      if (approve) {
        await api.approveTerminal(terminal.id);
      } else {
        await api.rejectTerminal(terminal.id, rejectReason.trim() || 'сдвояването не беше одобрено');
      }
      setMsg({
        tone: approve ? 'ok' : 'warn',
        text: approve
          ? `„${terminal.label}“ на ${terminal.merchant.name} е одобрен и вече може да приема плащания.`
          : `„${terminal.label}“ на ${terminal.merchant.name} е отхвърлен. Търговецът вижда причината и може да сдвои телефона отново.`,
      });
      setOpenReview(null);
      await load();
    } catch (err) {
      setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  }

  const rows = data?.data ?? [];
  const merchants = data?.merchants ?? [];

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <Note title="Отнемането минава през подписана команда">
        Терминалът е телефон на чужд бизнес. Отнемането му го спира при следващата му заявка,
        търговецът не може да го върне сам и затова действието се подписва с ключа на това
        устройство и влиза в одитната верига с вашето име - точно както спирането на търговец или
        рефундът. Записва се и при самия терминал, и в историята на търговеца.
      </Note>

      {signer.identity ? null : (
        <Note tone="warn" title="Този браузър няма ключ за подписване">
          Можете да четете всичко тук, но не можете да отнемете терминал. Създайте ключ от
          „Регистрирани устройства“.
        </Note>
      )}

      {msg ? <Note tone={msg.tone}>{msg.text}</Note> : null}

      <Panel title="Търговци с картови терминали" padded={false}>
        {merchants.length === 0 ? (
          <Empty>Нито един търговец няма функцията или регистриран телефон.</Empty>
        ) : (
          <TableWrap>
            <table className="wc-table">
              <thead>
                <tr>
                  <th>Търговец</th>
                  <th>Държава</th>
                  <th>Tap to Pay</th>
                  <th>Функцията</th>
                  <th>Терминали</th>
                </tr>
              </thead>
              <tbody>
                {merchants.map((m) => (
                  <tr key={m.id}>
                    <td>
                      {onOpenMerchant ? (
                        <button
                          type="button"
                          onClick={() => onOpenMerchant(m.id)}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'var(--wc-blue-ink)' }}
                        >
                          <strong>{m.name}</strong>
                        </button>
                      ) : (
                        <strong>{m.name}</strong>
                      )}
                      <div style={{ fontSize: 12, color: 'var(--wc-muted)' }}>{badgeLabel(m.status)}</div>
                    </td>
                    <td className="wc-num" style={{ fontSize: 13 }}>{m.country}</td>
                    <td>
                      {/*
                        Двете платформи поотделно, защото списъците на Stripe се
                        различават. Бизнес само с iPhone в държава, поддържана
                        единствено за Android, е блокиран, а един общ флаг
                        „поддържа се“ би скрил точно това.
                      */}
                      <Badge tone={m.tap_to_pay.unavailable ? 'fail' : m.tap_to_pay.ios && m.tap_to_pay.android ? 'ok' : 'warn'}>
                        {availabilityText(m.tap_to_pay)}
                      </Badge>
                      <div style={{ fontSize: 11, color: 'var(--wc-faint)' }}>
                        списъци от {m.tap_to_pay.listsCheckedOn}
                      </div>
                    </td>
                    <td>
                      <Badge tone={m.granted ? 'ok' : 'neutral'}>
                        {m.granted ? 'ПРЕДОСТАВЕНА' : 'НЯМА'}
                      </Badge>
                      {m.granted && m.granted_at ? (
                        <div style={{ fontSize: 11.5, color: 'var(--wc-muted)' }} title={m.granted_by ?? undefined}>
                          {relative(m.granted_at)}
                        </div>
                      ) : null}
                      {/*
                        Отнета функция при живи телефони е точно случаят, заради
                        който този списък включва и търговци без предоставяне -
                        иначе редът, който трябва да се види, изчезва.
                      */}
                      {!m.granted && m.terminals.active > 0 ? (
                        <div style={{ fontSize: 11.5, color: 'var(--wc-orange)' }}>
                          но има активни терминали
                        </div>
                      ) : null}
                    </td>
                    <td className="wc-num" style={{ fontSize: 13 }}>
                      {m.terminals.active} активни
                      {m.terminals.pending > 0 ? (
                        <span style={{ color: 'var(--wc-amber, #b45309)', fontWeight: 600 }}> · {m.terminals.pending} чакат преглед</span>
                      ) : null}
                      {m.terminals.revoked > 0 ? (
                        <span style={{ color: 'var(--wc-faint)' }}> · {m.terminals.revoked} отнети</span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Panel>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '0 0 240px' }}>
          <Field label="Търговец">
            <select className="wc-input" value={merchantId} onChange={(e) => setMerchantId(e.target.value)}>
              <option value="">Всички търговци</option>
              {merchants.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </Field>
        </div>
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <Field label="Търсене" hint="По име на терминал, търговец или модел на апарата.">
            <input
              className="wc-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="напр. каса, Pixel, Alpha"
            />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 8, paddingBottom: 14 }}>
          {[
            ['all', 'Всички'],
            ['ACTIVE', 'Активни'],
            ['REVOKED', 'Отнети'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`wc-btn wc-btn-sm ${status === key ? '' : 'wc-btn-secondary'}`}
              onClick={() => setStatus(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? <Spinner label="Зареждане…" /> : error ? <Note tone="danger">{error}</Note> : null}

      <Panel title={`Терминали · ${rows.length}`} padded={false}>
        {rows.length === 0 && !loading ? (
          <Empty>Няма терминали по този филтър.</Empty>
        ) : (
          <TableWrap>
            <table className="wc-table">
              <thead>
                <tr>
                  <th>Търговец</th>
                  <th>Терминал</th>
                  <th>Устройство</th>
                  <th>Регистриран</th>
                  <th>Последно видян</th>
                  <th>Последен проблем</th>
                  <th>Статус</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => {
                  const active = t.status === 'ACTIVE';
                  const open = confirming === t.id;
                  return (
                    <Fragment key={t.id}>
                      <tr style={{ opacity: active ? 1 : 0.65 }}>
                        <td style={{ fontSize: 13 }}>
                          {onOpenMerchant ? (
                            <button
                              type="button"
                              onClick={() => onOpenMerchant(t.merchant.id)}
                              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'var(--wc-blue-ink)', textAlign: 'left' }}
                            >
                              {t.merchant.name}
                            </button>
                          ) : (
                            t.merchant.name
                          )}
                          <div className="wc-num" style={{ fontSize: 11.5, color: 'var(--wc-faint)' }}>
                            {t.merchant.country}
                          </div>
                        </td>
                        <td>
                          <strong style={{ fontSize: 13.5 }}>{t.label}</strong>
                          <div className="wc-num" style={{ fontSize: 11, color: 'var(--wc-faint)' }}>
                            {shortId(t.id, 5)}
                          </div>
                        </td>
                        <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>
                          {deviceLine(t)}
                          {/*
                            Идентификаторът на инсталацията е уникален за цялата
                            платформа, тоест назовава апарата, а не регистрацията -
                            това е, по което се намира чий е един предаден или
                            откраднат телефон.
                          */}
                          <div className="wc-num" style={{ fontSize: 11, color: 'var(--wc-faint)' }} title={t.install_id}>
                            {shortInstall(t.install_id)}
                          </div>
                        </td>
                        <td style={{ fontSize: 13, color: 'var(--wc-muted)' }} title={`${dateTime(t.registered_at)} · ${t.registered_by}`}>
                          {relative(t.registered_at)}
                          <div className="wc-num" style={{ fontSize: 11, color: 'var(--wc-faint)' }}>
                            {shortId(t.registered_by, 4)}
                          </div>
                        </td>
                        <td style={{ fontSize: 13, color: 'var(--wc-muted)' }}>
                          {t.last_seen_at ? relative(t.last_seen_at) : 'никога'}
                        </td>
                        {/*
                          Каквото е видял операторът, дословно. Без тази колона
                          един отказан терминал изглежда еднакво независимо дали
                          сумата е под минимума на Stripe, дали са включени
                          опциите за разработчик, или картата е вдигната рано.
                          Пълният списък е в „История“ отдясно.
                        */}
                        <td style={{ fontSize: 12.5, maxWidth: 240 }}>
                          {t.last_problem ? (
                            <>
                              <div
                                style={{
                                  color: t.last_problem.permanent ? 'var(--wc-danger)' : 'var(--wc-muted)',
                                  fontWeight: t.last_problem.permanent ? 600 : 400,
                                }}
                              >
                                {t.last_problem.message}
                              </div>
                              <div
                                className="wc-num"
                                style={{ fontSize: 11, color: 'var(--wc-faint)', marginTop: 2 }}
                                title={`${dateTime(t.last_problem.at)} · ${t.last_problem.code} · ${t.last_problem.actor}`}
                              >
                                {relative(t.last_problem.at)} · {t.last_problem.stage}
                              </div>
                            </>
                          ) : (
                            <span style={{ color: 'var(--wc-faint)' }}>няма</span>
                          )}
                        </td>
                        <td>
                          <Badge tone={active ? 'ok' : 'neutral'}>{badgeLabel(t.status)}</Badge>
                          {t.revoked_reason ? (
                            <div style={{ fontSize: 11.5, color: 'var(--wc-muted)' }}>{t.revoked_reason}</div>
                          ) : null}
                        </td>
                        <td className="wc-right">
                          <div style={{ display: 'inline-flex', gap: 6 }}>
                            <button
                              type="button"
                              className="wc-btn wc-btn-sm wc-btn-secondary"
                              onClick={() => (openHistory === t.id ? setOpenHistory(null) : void showHistory(t.id))}
                            >
                              {openHistory === t.id ? 'Скрий' : 'История'}
                            </button>
                            {t.status === 'PENDING_REVIEW' ? (
                              <button
                                type="button"
                                className="wc-btn wc-btn-sm wc-btn-primary"
                                disabled={busy === t.id}
                                onClick={() => (openReview === t.id ? setOpenReview(null) : void showReview(t.id))}
                              >
                                {openReview === t.id ? 'Скрий' : 'Прегледай'}
                              </button>
                            ) : null}
                            {active && !open ? (
                              <button
                                type="button"
                                className="wc-btn wc-btn-sm wc-btn-danger"
                                disabled={busy === t.id}
                                onClick={() => { setConfirming(t.id); setReason(''); }}
                              >
                                Отнеми
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>

                      {openReview === t.id ? (
                        <tr>
                          <td colSpan={8} style={{ background: 'var(--wc-blue-tint, var(--wc-grey-tint))' }}>
                            <div style={{ padding: '6px 0 10px' }}>
                              {!review ? (
                                <Spinner label="Зареждане на данните за преглед…" />
                              ) : (
                                <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr', maxWidth: 720 }}>
                                  <div>
                                    <div style={{ fontSize: 12, color: 'var(--wc-faint)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Устройство</div>
                                    <div style={{ fontSize: 13.5, marginTop: 4 }}>
                                      {[review.terminal.device_model, review.terminal.platform?.toUpperCase(), review.terminal.os_version].filter(Boolean).join(' · ') || '-'}
                                    </div>
                                    <div className="wc-num" style={{ fontSize: 11.5, color: 'var(--wc-faint)', marginTop: 2 }}>
                                      install {review.terminal.install_id.slice(0, 12)}…
                                    </div>
                                    <div style={{ fontSize: 12.5, color: 'var(--wc-muted)', marginTop: 6 }}>
                                      {review.merchant.name} · {review.merchant.country} ·{' '}
                                      {review.merchant.charges_enabled ? 'плащанията са включени' : 'плащанията не са включени'}
                                    </div>
                                  </div>
                                  <div>
                                    <div style={{ fontSize: 12, color: 'var(--wc-faint)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                      Адрес, проверен от Stripe ({review.mode === 'test' ? 'тест' : 'реален'})
                                    </div>
                                    {review.verified_address ? (
                                      <div style={{ fontSize: 13.5, marginTop: 4, lineHeight: 1.5 }}>
                                        {review.verified_address.line1}
                                        {review.verified_address.line2 ? <>, {review.verified_address.line2}</> : null}<br />
                                        {[review.verified_address.city, review.verified_address.state, review.verified_address.postal_code].filter(Boolean).join(', ')}<br />
                                        {review.verified_address.country}
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: 13, color: 'var(--wc-danger)', marginTop: 4 }}>
                                        {review.address_error ?? 'Няма адрес.'}
                                      </div>
                                    )}
                                    <div style={{ fontSize: 11.5, color: 'var(--wc-faint)', marginTop: 6 }}>
                                      Локацията на терминала се създава от този адрес. Одобрете само ако съответства на бизнеса и документите.
                                    </div>
                                  </div>
                                  <div style={{ gridColumn: '1 / -1' }}>
                                    <Field label="Причина при отхвърляне" hint="Записва се в одита и се показва на търговеца. Не е нужна при одобрение.">
                                      <input
                                        className="wc-input"
                                        value={rejectReason}
                                        onChange={(e) => setRejectReason(e.target.value)}
                                        placeholder="напр. адресът не съвпада с документите"
                                        maxLength={200}
                                      />
                                    </Field>
                                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                      <button
                                        type="button"
                                        className="wc-btn wc-btn-sm wc-btn-primary"
                                        disabled={busy === t.id}
                                        onClick={() => void decide(t, true)}
                                      >
                                        {busy === t.id ? 'Одобряване…' : 'Одобри'}
                                      </button>
                                      <button
                                        type="button"
                                        className="wc-btn wc-btn-sm wc-btn-danger"
                                        disabled={busy === t.id}
                                        onClick={() => void decide(t, false)}
                                      >
                                        Отхвърли
                                      </button>
                                      <button
                                        type="button"
                                        className="wc-btn wc-btn-sm wc-btn-secondary"
                                        disabled={busy === t.id}
                                        onClick={() => setOpenReview(null)}
                                      >
                                        Затвори
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ) : null}

                      {open ? (
                        <tr>
                          <td colSpan={8} style={{ background: 'var(--wc-amber-tint)' }}>
                            <div style={{ padding: '4px 0 8px' }}>
                              <p style={{ margin: '0 0 8px', fontSize: 13.5 }}>
                                „{t.label}“ на <strong>{t.merchant.name}</strong> ще спре да приема
                                плащания при следващата си заявка. Вече взетите плащания не се
                                засягат. Търговецът вижда отнемането и причината в своя панел, но не
                                може да върне терминала - трябва да сдвои телефона отново.
                              </p>
                              <Field label="Причина" hint="Записва се в одита и се показва на търговеца.">
                                <input
                                  className="wc-input"
                                  value={reason}
                                  onChange={(e) => setReason(e.target.value)}
                                  placeholder="напр. телефонът е обявен за откраднат от търговеца"
                                  maxLength={200}
                                />
                              </Field>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                  type="button"
                                  className="wc-btn wc-btn-sm wc-btn-danger"
                                  disabled={busy === t.id || !signer.identity}
                                  onClick={() => void revoke(t)}
                                >
                                  {busy === t.id ? 'Подписване…' : 'Подпиши и отнеми'}
                                </button>
                                <button
                                  type="button"
                                  className="wc-btn wc-btn-sm wc-btn-secondary"
                                  disabled={busy === t.id}
                                  onClick={() => setConfirming(null)}
                                >
                                  Откажи
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}

                      {openHistory === t.id ? (
                        <tr>
                          <td colSpan={8} style={{ background: 'var(--wc-grey-tint)' }}>
                            {!history ? (
                              <Spinner label="Зареждане на историята…" />
                            ) : history.data.length === 0 ? (
                              <Empty>Няма записи за този терминал.</Empty>
                            ) : (
                              <div style={{ display: 'grid', gap: 6, padding: '4px 0 8px' }}>
                                {history.data.map((e) => (
                                  <div key={e.sequence} style={{ fontSize: 13 }}>
                                    <span className="wc-num" style={{ color: 'var(--wc-faint)' }}>#{e.sequence}</span>{' '}
                                    <strong>{e.action}</strong>{' '}
                                    <span style={{ color: 'var(--wc-muted)' }}>{dateTime(e.at)}</span>{' '}
                                    <span className="wc-num" style={{ fontSize: 11.5, color: 'var(--wc-faint)' }}>
                                      {e.actor}
                                    </span>
                                    {ACTION_NOTE[e.action] ? (
                                      <div style={{ fontSize: 12.5, color: 'var(--wc-muted)' }}>
                                        {ACTION_NOTE[e.action]}
                                      </div>
                                    ) : null}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>
        )}
        <p style={{ padding: '10px 16px', margin: 0, fontSize: 12.5, color: 'var(--wc-faint)', lineHeight: 1.55 }}>
          Отнетите редове остават. Терминал, който изчезва от списъка, не оставя нищо, с което да се
          обясни плащане, взето през него миналата седмица. Един апарат може да държи само един
          терминал наведнъж, така че отнет телефон е свободен да бъде сдвоен отново - при същия или
          при друг търговец.
        </p>
      </Panel>
    </div>
  );
}
